import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { primaryAuth } from '../firebase';

/**
 * useAuthClaims — reads the signed-in user's Firebase Auth custom claims
 * (root / superAdmin / admin) and re-evaluates on auth state changes.
 *
 * Custom claims live on the ID token, not on the user record itself,
 * so we call `getIdTokenResult` after each auth state change to extract
 * them. The token refresh is cheap (cached client-side until the token
 * expires) but happens every mount of any component using this hook —
 * fine for nav-level gating, don't use inside tight render loops.
 *
 * Returns:
 *   {
 *     ready: boolean,         // false until first onAuthStateChanged fires
 *     root: boolean,          // ATACT staff, cross-site (accessLevel 1-10)
 *     superAdmin: boolean,    // per-site top admin
 *     admin: boolean,         // per-site reviewer
 *     uid: string | null,
 *   }
 *
 * Usage:
 *   const { root } = useAuthClaims();
 *   if (root) { ...show staff nav... }
 *
 * Added rev 77 (2026-05-15) for the "Manage Sites" tab gating.
 */
export default function useAuthClaims() {
  const [state, setState] = useState({
    ready: false,
    root: false,
    superAdmin: false,
    admin: false,
    uid: null,
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(primaryAuth, async (user) => {
      if (!user) {
        setState({
          ready: true,
          root: false,
          superAdmin: false,
          admin: false,
          uid: null,
        });
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        const claims = tokenResult.claims || {};
        setState({
          ready: true,
          root: claims.root === true,
          superAdmin: claims.superAdmin === true,
          admin: claims.admin === true,
          uid: user.uid,
        });
      } catch (e) {
        console.warn('-- useAuthClaims: failed to read token claims', e);
        setState({
          ready: true,
          root: false,
          superAdmin: false,
          admin: false,
          uid: user.uid,
        });
      }
    });
    return () => unsub();
  }, []);

  return state;
}
